import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit';
import { IResponse } from 'types';
import React from 'react';
import { callEndpoint } from 'redux/global';
import { RootState } from 'redux/store';

export type Metric = {
  startDate: Date,
  endDate: Date
}
export type ModalParts = {
  component: React.JSX.Element,
  exitOnBgClick?: Function,
  noBg?: boolean,
  skipSideBar?: true
}

export type BranchPosLock = {
  email: string
  username?: string
  screen: 'sales' | 'refunds'
  /** ISO timestamp — lock is free after this without a heartbeat. */
  expiresAt: string
}

export type Branch = {
  name: string,
  staff: string[],
  /** Soft-deleted: hidden from Departments UI, kept for audit history. */
  isDeleted?: boolean,
  /** Exclusive Sales/Refunds session for this department (ephemeral). */
  posLock?: BranchPosLock | null,
}

export type InventoryItem = {
  id: string,
  name: string,
  price: number,
  type: string,
  description?: string,
  unit?: string,
  image?: string,
  /** Optional product barcode / UPC / EAN for scanner selection. */
  barcode?: string,
  /** When true, item is an in-house expense (not sold to guests). */
  isInhouseExpense?: boolean,
  /** Units on hand for guest-sellable items. */
  stock?: number,
  createdAt: string,
  updatedAt: string,
}

/** Expense / in-house items: flagged or stored with a negative price. */
export const isExpenseInventoryItem = (item: Pick<InventoryItem, 'price' | 'isInhouseExpense'>) => (
  Boolean(item.isInhouseExpense) || Number(item.price) < 0
);

type SubscriptionPlan = {
  hotelId: string,
  planId: string,
  status: string,
  planTitle: string,
  nextPaymentDate: string,
  createdAt: string,
  paystack_subscription: any
}

/** Default turnover buffer when prefs omit / invalidate houseKeepingMinutes. */
export const DEFAULT_HOUSE_KEEPING_MINUTES = 30;
/** Cap turnover buffer so availability math stays practical (8 hours). */
export const MAX_HOUSE_KEEPING_MINUTES = 480;

/** Inclusive HH:MM window for a meal service period. */
export type MealWindow = {
  start: string;
  end: string;
};

export type MealTimesPrefs = {
  breakfast: MealWindow;
  lunch: MealWindow;
  dinner: MealWindow;
};

/** Matches legacy hardcoded getMealNow hour ranges (inclusive). */
export const DEFAULT_MEAL_TIMES: MealTimesPrefs = {
  breakfast: { start: '07:00', end: '11:59' },
  lunch: { start: '12:00', end: '16:59' },
  dinner: { start: '17:00', end: '23:59' },
};

const MEAL_TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

/** Normalize browser time values (may include seconds) to HH:MM. */
export const normalizeMealClock = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  // Accept HH:MM or HH:MM:SS from <input type="time">
  const match = trimmed.match(/^([01]?\d|2[0-3]):([0-5]\d)(?::[0-5]\d)?$/);
  if (!match) return null;
  return `${match[1].padStart(2, '0')}:${match[2]}`;
};

/** Parse "HH:MM" → minutes from midnight; null if invalid. */
export const parseMealClock = (value: unknown): number | null => {
  const normalized = normalizeMealClock(value);
  if (!normalized || !MEAL_TIME_RE.test(normalized)) return null;
  const [h, m] = normalized.split(':').map(Number);
  return h * 60 + m;
};

export const isValidMealWindow = (w: unknown): w is MealWindow => {
  if (!w || typeof w !== 'object') return false;
  const start = normalizeMealClock((w as MealWindow).start);
  const end = normalizeMealClock((w as MealWindow).end);
  // start may be after end (overnight); only clock format is required.
  return !!start && !!end;
};

/** Expand a window to one or two inclusive minute ranges (handles overnight). */
const mealWindowRanges = (w: MealWindow): Array<[number, number]> => {
  const start = parseMealClock(w.start);
  const end = parseMealClock(w.end);
  if (start === null || end === null) return [];
  if (start <= end) return [[start, end]];
  // Overnight: e.g. 22:00–02:00 → [22:00, 23:59] and [00:00, 02:00]
  return [[start, 24 * 60 - 1], [0, end]];
};

const linearRangesOverlap = (a: [number, number], b: [number, number]): boolean => (
  a[0] <= b[1] && b[0] <= a[1]
);

/** Inclusive ranges overlap when both cover any shared minute (overnight-aware). */
const mealWindowsOverlap = (a: MealWindow, b: MealWindow): boolean => {
  const aRanges = mealWindowRanges(a);
  const bRanges = mealWindowRanges(b);
  if (!aRanges.length || !bRanges.length) return true;
  return aRanges.some((ar) => bRanges.some((br) => linearRangesOverlap(ar, br)));
};

/**
 * Returns a short error when any two meal windows overlap; null when clear.
 * Touches only when each individual window is already valid.
 */
export const getMealTimesOverlapError = (times: MealTimesPrefs): string | null => {
  if (!isValidMealWindow(times.breakfast)
    || !isValidMealWindow(times.lunch)
    || !isValidMealWindow(times.dinner)) {
    return null;
  }
  if (mealWindowsOverlap(times.breakfast, times.lunch)) {
    return 'Breakfast and Lunch times overlap';
  }
  if (mealWindowsOverlap(times.breakfast, times.dinner)) {
    return 'Breakfast and Dinner times overlap';
  }
  if (mealWindowsOverlap(times.lunch, times.dinner)) {
    return 'Lunch and Dinner times overlap';
  }
  return null;
};

/** True when nowMins falls inside an inclusive (possibly overnight) window. */
export const isNowInMealWindow = (start: string, end: string, nowMins: number): boolean => {
  const s = parseMealClock(start);
  const e = parseMealClock(end);
  if (s === null || e === null) return false;
  if (s <= e) return nowMins >= s && nowMins <= e;
  // Overnight wrap
  return nowMins >= s || nowMins <= e;
};

/** Which guest contact fields are required (and thus shown) on booking modals. */
export type RequiredContactFields = {
  email: boolean;
  phone: boolean;
  whatsapp: boolean;
  telegram: boolean;
};

export type HotelPrefs = {
  bookingIdDocRequired?: boolean;
  /**
   * Contact fields that must be collected on booking/check-in modals.
   * At least one must be true. Non-required fields are hidden from those modals.
   */
  requiredContactFields?: Partial<RequiredContactFields>;
  /**
   * Minutes after checkout before the room is free to book again.
   * Also used for imminent check-in / key-card windows.
   */
  houseKeepingMinutes?: number;
  /** Breakfast / lunch / dinner service windows (HH:MM inclusive). */
  mealTimes?: MealTimesPrefs;
  /** Named inventory categories available even when no items use them yet. */
  inventoryTypes?: string[];
  /**
   * Inventory group name → department names that may sell/use it.
   * Missing key or empty array means all departments.
   */
  inventoryTypeDepartments?: Record<string, string[]>;
};

/** Default: email only (phone/WhatsApp/Telegram opt-in via Property settings). */
export const DEFAULT_REQUIRED_CONTACT_FIELDS: RequiredContactFields = {
  email: true,
  phone: false,
  whatsapp: false,
  telegram: false,
};

export const getRequiredContactFields = (
  prefs?: HotelPrefs | object | null,
): RequiredContactFields => {
  const raw = (prefs && typeof prefs === 'object')
    ? (prefs as HotelPrefs).requiredContactFields
    : undefined;
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_REQUIRED_CONTACT_FIELDS };
  const next: RequiredContactFields = {
    email: raw.email === true,
    phone: raw.phone === true,
    whatsapp: raw.whatsapp === true,
    telegram: raw.telegram === true,
  };
  // Never allow an empty set — fall back to email required.
  if (!next.email && !next.phone && !next.whatsapp && !next.telegram) {
    return { ...DEFAULT_REQUIRED_CONTACT_FIELDS };
  }
  return next;
};

export const getBookingIdDocRequired = (prefs?: HotelPrefs | object | null): boolean => {
  if (!prefs || typeof prefs !== 'object') return true;
  return (prefs as HotelPrefs).bookingIdDocRequired !== false;
};

/** Clamp prefs value; fall back to default when missing/invalid. */
export const getHouseKeepingMinutes = (prefs?: HotelPrefs | object | null): number => {
  if (!prefs || typeof prefs !== 'object') return DEFAULT_HOUSE_KEEPING_MINUTES;
  const mins = (prefs as HotelPrefs).houseKeepingMinutes;
  if (typeof mins !== 'number' || !Number.isFinite(mins)) return DEFAULT_HOUSE_KEEPING_MINUTES;
  return Math.min(MAX_HOUSE_KEEPING_MINUTES, Math.max(1, Math.round(mins)));
};

export const getHouseKeepingTimeMs = (prefs?: HotelPrefs | object | null): number => (
  getHouseKeepingMinutes(prefs) * 60 * 1000
);

/** Resolve meal windows from prefs; invalid/missing slots fall back to defaults. */
export const getMealTimes = (prefs?: HotelPrefs | object | null): MealTimesPrefs => {
  const raw = (prefs && typeof prefs === 'object')
    ? (prefs as HotelPrefs).mealTimes
    : undefined;
  const pick = (slot: MealWindow | undefined, fallback: MealWindow): MealWindow => {
    if (!isValidMealWindow(slot)) return fallback;
    return {
      start: normalizeMealClock(slot.start) || fallback.start,
      end: normalizeMealClock(slot.end) || fallback.end,
    };
  };
  return {
    breakfast: pick(raw?.breakfast, DEFAULT_MEAL_TIMES.breakfast),
    lunch: pick(raw?.lunch, DEFAULT_MEAL_TIMES.lunch),
    dinner: pick(raw?.dinner, DEFAULT_MEAL_TIMES.dinner),
  };
};

/** Normalize + validate a full mealTimes patch; null if invalid or overlapping. */
export const normalizeMealTimesPatch = (patch: unknown): MealTimesPrefs | null => {
  if (!patch || typeof patch !== 'object') return null;
  const p = patch as Partial<MealTimesPrefs>;
  if (!isValidMealWindow(p.breakfast) || !isValidMealWindow(p.lunch) || !isValidMealWindow(p.dinner)) {
    return null;
  }
  const normalized: MealTimesPrefs = {
    breakfast: {
      start: normalizeMealClock(p.breakfast.start) as string,
      end: normalizeMealClock(p.breakfast.end) as string,
    },
    lunch: {
      start: normalizeMealClock(p.lunch.start) as string,
      end: normalizeMealClock(p.lunch.end) as string,
    },
    dinner: {
      start: normalizeMealClock(p.dinner.start) as string,
      end: normalizeMealClock(p.dinner.end) as string,
    },
  };
  // Reject overlapping service windows so getMealNow never has ambiguous periods.
  if (getMealTimesOverlapError(normalized)) return null;
  return normalized;
};

export type ImageCredit = {
  balance: number;
  created_as_of: string;
};

export type HotelData = {
  id: string,
  name: string,
  password: string,
  nameSave?: string,
  address: string,
  email: string,
  username?: string,
  linkedin?: string | null,
  facebook?: string | null,
  instagram?: string | null,
  twitter?: string | null,
  phoneNumber: string,
  logo?: string,
  accNumber?: string | null,
  accName?: string | null,
  field1?: string | null,
  field2?: string | null,
  currency?: string | null,
  displayEmail?: string,
  prefs: HotelPrefs,
  branches: Branch[],
  inventory?: InventoryItem[],
  fields: object,
  deal: string,
  country: string,
  region: string,
  branch: string,
  expires?: string,
  billingDate?: string,
  suffix?: string | null,
  city: string,
  coId?: string,
  channelExpiry?: string,
  keyCardUrl?: string,
  keyCardExpiry?: string,
  roomPlan?: string,
  roomSubscription?: SubscriptionPlan,
  channelManagerSubscription?: SubscriptionPlan,
  channelManagerPlan?: string,
  logsPlan?: string,
  logsSubscription?: SubscriptionPlan,
  maxLogs?: number | null,
  storagePlan?: string,
  storageSubscription?: SubscriptionPlan,
  maxStorage?: number | null,
  seatsPlan?: string,
  seatsSubscription?: SubscriptionPlan,
  maxStaff?: number | null,
  maxEmailCampaigns?: number;
  /** AI inventory image packs; each entry is a purchased credit pack. */
  imageCredits?: ImageCredit[];
  modifiedAt?: string;
}

export interface ViewsState {
  socket: any,
  isSocketConnected: boolean,
  metricTime: Metric | null,
  debugText: any[],
  hotelData: HotelData | null,
  modalParts: ModalParts | null,
  isSyncCORooms: boolean,
  isShowSideBar: string[],
  layoutRoomChargePanel: {
    roomId: string,
    bookingId: string,
    /** Alert pay: session-mock settle only (charge + alert return next sign-in). */
    mockSessionSettle?: boolean,
  } | null,
  rightBarDismissTick: number,
}

const oneMonthAgo = new Date();
oneMonthAgo.setDate(oneMonthAgo.getDate() - 30);
oneMonthAgo.setHours(0, 0, 0, 0);
const lateEndD = new Date();
lateEndD.setHours(23, 59, 59, 999);

const initialState: ViewsState = {
  socket: null,
  isSocketConnected: true,
  metricTime: {
    startDate: oneMonthAgo,
    endDate: lateEndD,
  },
  debugText: [],
  hotelData: null,
  modalParts: null,
  isSyncCORooms: false,
  isShowSideBar: [],
  layoutRoomChargePanel: null,
  rightBarDismissTick: 0,
};

export const counterSlice = createSlice({
  name: 'views',
  initialState,
  reducers: {
    setSocket: (state, action) => {
      state.socket = action.payload;
    },
    setIsSocketConnected: (state, action: PayloadAction<boolean>) => {
      state.isSocketConnected = action.payload;
    },
    setMetricTime: (state, action: PayloadAction<Metric | null>) => {
      state.metricTime = action.payload;
    },
    setDebugText: (state, action: PayloadAction<any[]>) => {
      state.debugText = action.payload;
    },
    setHotelData: (state, action: PayloadAction<HotelData | null>) => {
      if (action.payload) {
        localStorage.setItem('hotel_data_id', action.payload.id.toString());
      } else {
        localStorage.removeItem('hotel_data_id');
      }
      state.hotelData = action.payload;
    },
    setModalParts: (state, action: PayloadAction<ModalParts | null>) => {
      state.modalParts = action.payload;
    },
    setIsShowSideBar: (state, action: PayloadAction<boolean | string[]>) => {
      if (typeof action.payload === 'boolean') {
        state.isShowSideBar = action.payload ? ['main'] : [];
      } else {
        state.isShowSideBar = action.payload;
      }
    },
    addIsShowSideBarKey: (state, action: PayloadAction<string>) => {
      if (!state.isShowSideBar.includes(action.payload)) {
        state.isShowSideBar.push(action.payload);
      }
    },
    removeIsShowSideBarKey: (state, action: PayloadAction<string>) => {
      state.isShowSideBar = state.isShowSideBar.filter((item) => item !== action.payload);
    },
    setIsSyncCORooms: (state, action: PayloadAction<boolean>) => {
      state.isSyncCORooms = action.payload;
    },
    setLayoutRoomChargePanel: (
      state,
      action: PayloadAction<{
        roomId: string,
        bookingId: string,
        mockSessionSettle?: boolean,
      } | null>,
    ) => {
      state.layoutRoomChargePanel = action.payload;
    },
    bumpRightBarDismiss: (state) => {
      state.rightBarDismissTick += 1;
    },
  },
});

export const {
  setSocket,
  setIsSocketConnected,
  setMetricTime,
  setDebugText,
  setHotelData,
  setModalParts,
  setIsShowSideBar,
  addIsShowSideBarKey,
  removeIsShowSideBarKey,
  setIsSyncCORooms,
  setLayoutRoomChargePanel,
  bumpRightBarDismiss,
} = counterSlice.actions;

export const selectSocket = (state: RootState) => state.views.socket;
export const selectIsSocketConnected = (state: RootState) => state.views.isSocketConnected;
export const selectMetricTime = (state: RootState) => state.views.metricTime;
export const selectDebugText = (state: RootState) => state.views.debugText;
export const selectHotelData = (state: RootState) => state.views.hotelData;
export const selectModalParts = (state: RootState) => state.views.modalParts;
export const selectCurrency = (state: RootState) => state.views.hotelData?.currency || '$';
export const selectBookingIdDocRequired = (state: RootState) =>
  getBookingIdDocRequired(state.views.hotelData?.prefs);
/** Memoized — getRequiredContactFields always builds a new object; unstable refs cancel ID-doc fetches. */
export const selectRequiredContactFields = createSelector(
  [(state: RootState) => state.views.hotelData?.prefs],
  (prefs) => getRequiredContactFields(prefs),
);
export const selectHouseKeepingTimeMs = (state: RootState) =>
  getHouseKeepingTimeMs(state.views.hotelData?.prefs);
export const selectHouseKeepingMinutes = (state: RootState) =>
  getHouseKeepingMinutes(state.views.hotelData?.prefs);
export const selectMealTimes = (state: RootState) =>
  getMealTimes(state.views.hotelData?.prefs);
export const selectIsSyncCORooms = (state: RootState) => state.views.isSyncCORooms;
export const selectIsShowSideBar = (state: RootState) => state.views.isShowSideBar;
export const selectLayoutRoomChargePanel = (state: RootState) => state.views.layoutRoomChargePanel;
export const selectRightBarDismissTick = (state: RootState) => state.views.rightBarDismissTick;

export default counterSlice.reducer;

export const setBranches = (branches: string) => async (dispatch: Function): Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'setbranches',
      method: 'POST',
      body: { branches }
    }),
  );
  return res;
};

export const claimPosLock = (
  department: string,
  screen: 'sales' | 'refunds',
) => async (dispatch: Function): Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'claimposlock',
      method: 'POST',
      body: { department, screen },
    }),
  );
  return res;
};

export const heartbeatPosLock = (department: string) => async (dispatch: Function): Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'heartbeatposlock',
      method: 'POST',
      body: { department },
    }),
  );
  return res;
};

export const releasePosLock = (department: string) => async (dispatch: Function): Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'releaseposlock',
      method: 'POST',
      body: { department },
    }),
  );
  return res;
};

export const setInventory = (inventory: string) => async (dispatch: Function): Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'setinventory',
      method: 'POST',
      body: { inventory }
    }),
  );
  return res;
};

export const addTmpHotel = (hotelData: HotelData) => async (dispatch: Function)
: Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'addTMPhotel',
      method: 'POST',
      body: { hotelData }
    }),
  );
  return res;
};

export const transferTmpHotel = (id: string) => async (dispatch: Function)
: Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'transferTMPhotel',
      method: 'POST',
      body: { id }
    }),
  );
  return res;
};

export const handlePropertyCO = () => async (dispatch: Function)
: Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'handleproperty',
      method: 'GET'
    }),
  );
  return res;
};
